// ═══════════════════════════════════════════════════════════════
// Intake — Roster (Individual List)
// Searchable, filterable, sortable table with detail modal
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Avatar,
  Table, SegmentedControl, Stack, Select, Center, ThemeIcon,
  Modal, Textarea, SimpleGrid, Divider, Pagination,
} from '../../components/gab/GabComponents';

const ROSTER_PAGE_SIZE = 25;
import { notifications } from '../../lib/notifications';
import {
  IconSearch, IconUserPlus, IconAlertTriangle,
  IconSortAscending, IconSortDescending, IconPhone, IconNotes,
  IconMedicalCross, IconPill, IconUser, IconPrinter, IconEdit, IconDeviceFloppy, IconTrash,
  IconCamera, IconUpload, IconX, IconDownload,
} from '@tabler/icons-react';
import { exportCSV } from '../../lib/exportCSV';
import { useIntakeStore } from '../../store/intakeStore';
import { useAuthStore } from '../../store/authStore';
import type { Individual } from '../../store/intakeStore';
import { formatDateTime, calcAge, TRIAGE_COLORS, resizeImageToBase64 } from './lib/helpers';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';

type SortKey = 'lastName' | 'dob' | 'triage' | 'tagCode' | 'status' | 'intakeMethod' | 'datecreated';

const TRIAGE_OPTIONS = [
  { value: 'var(--color-success)', label: 'Green', color: 'var(--color-success)' },
  { value: '#C68700', label: 'Yellow', color: '#C68700' },
  { value: 'var(--color-error)', label: 'Red', color: 'var(--color-error)' },
] as const;

// ── Code 128B barcode encoder ──────────────────────────────────
const CODE128B_START = 104;
const CODE128_STOP  = 106;
const CODE128_PATTERNS = [
  '11011001100','11001101100','11001100110','10010011000','10010001100',
  '10001001100','10011001000','10011000100','10001100100','11001001000',
  '11001000100','11000100100','10110011100','10011011100','10011001110',
  '10111001100','10011101100','10011100110','11001110010','11001011100',
  '11001001110','11011100100','11001110100','11100101100','11100100110',
  '11101100100','11100110100','11100110010','11011011000','11011000110',
  '11000110110','10100011000','10001011000','10001000110','10110001000',
  '10001101000','10001100010','11010001000','11000101000','11000100010',
  '10110111000','10110001110','10001101110','10111011000','10111000110',
  '10001110110','11101110110','11010001110','11000101110','11011101000',
  '11011100010','11011101110','11101011000','11101000110','11100010110',
  '11101101000','11101100010','11100011010','11101111010','11001000010',
  '11110001010','10100110000','10100001100','10010110000','10010000110',
  '10000101100','10000100110','10110010000','10110000100','10011010000',
  '10011000010','10000110100','10000110010','11000010010','11001010000',
  '11110111010','11000010100','10001111010','10100111100','10010111100',
  '10010011110','10111100100','10011110100','10011110010','11110100100',
  '11110010100','11110010010','11011011110','11011110110','11110110110',
  '10101111000','10100011110','10001011110','10111101000','10111100010',
  '11110101000','11110100010','10111011110','10111101110','11101011110',
  '11110101110','11010000100','11010010000','11010011100','1100011101011',
];

function encodeCode128B(text: string) {
  const codes = [CODE128B_START];
  for (let i = 0; i < text.length; i++) codes.push(text.charCodeAt(i) - 32);
  let checksum = codes[0];
  for (let i = 1; i < codes.length; i++) checksum += codes[i] * i;
  codes.push(checksum % 103);
  codes.push(CODE128_STOP);
  return codes.map(c => CODE128_PATTERNS[c]).join('');
}

function drawBarcode(pdf: jsPDF, text: string, x: number, y: number, width: number, height: number) {
  const pattern = encodeCode128B(text);
  const barWidth = width / pattern.length;
  pdf.setFillColor(17, 45, 78);
  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i] === '1') pdf.rect(x + i * barWidth, y, barWidth, height, 'F');
  }
}

/** Print a single ID-style badge for one individual */
export async function printBadge(person: Individual, incidentName = '') {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [101.6, 152.4] }); // 4x6 inch
  const w = 101.6, h = 152.4;
  const navy: [number, number, number] = [17, 45, 78];
  const triageColors: Record<string, [number, number, number]> = {
    red: [220, 38, 38], yellow: [217, 119, 6], green: [22, 163, 74],
  };
  const tc = triageColors[(person.triage || 'var(--color-success)').toLowerCase()] || navy;

  // Background
  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, w, h, 'F');

  // Top color banner
  pdf.setFillColor(...navy);
  pdf.rect(0, 0, w, 22, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(255, 255, 255);
  pdf.text('OpenGov EMC', w / 2, 9, { align: 'center' });
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text(incidentName || 'Emergency Management', w / 2, 16, { align: 'center' });

  // Photo or avatar placeholder
  const photoY = 28;
  if (person.photoUrl && person.photoUrl.startsWith('data:')) {
    try { pdf.addImage(person.photoUrl, 'JPEG', (w - 32) / 2, photoY, 32, 32); } catch { /* skip */ }
  } else {
    pdf.setFillColor(226, 232, 240);
    pdf.roundedRect((w - 32) / 2, photoY, 32, 32, 3, 3, 'F');
    pdf.setFontSize(20);
    pdf.setTextColor(148, 163, 184);
    pdf.text(((person.firstName?.[0] || '') + (person.lastName?.[0] || '')).toUpperCase(), w / 2, photoY + 20, { align: 'center' });
  }

  // Name
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.setTextColor(...navy);
  const fullName = `${person.firstName || ''} ${person.lastName || ''}`.trim();
  pdf.text(fullName, w / 2, 70, { align: 'center' });

  // DOB / Age / Gender line
  const age = calcAge(person.dob);
  const infoLine = [
    person.dob ? `DOB: ${new Date(person.dob).toLocaleDateString()}` : '',
    age != null ? `Age: ${age}` : '',
    person.gender || '',
  ].filter(Boolean).join('  ·  ');
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(100, 116, 139);
  if (infoLine) pdf.text(infoLine, w / 2, 77, { align: 'center' });

  // Triage banner
  pdf.setFillColor(...tc);
  pdf.roundedRect(20, 82, w - 40, 10, 2, 2, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(255, 255, 255);
  pdf.text(`TRIAGE: ${(person.triage || 'GREEN').toUpperCase()}`, w / 2, 89, { align: 'center' });

  // Tag code
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(18);
  pdf.setTextColor(...navy);
  pdf.text(person.tagCode || '', w / 2, 103, { align: 'center' });

  // Barcode
  drawBarcode(pdf, person.tagCode || '', 16, 107, w - 32, 10);

  // QR code
  try {
    const qrDataUrl = await QRCode.toDataURL(person.tagCode || 'N/A', {
      width: 300, margin: 1, errorCorrectionLevel: 'M',
      color: { dark: '#112D4E', light: '#ffffff' },
    });
    pdf.addImage(qrDataUrl, 'PNG', (w - 26) / 2, 120, 26, 26);
  } catch { /* skip */ }

  // Bottom text
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7);
  pdf.setTextColor(148, 163, 184);
  pdf.text(`Checked in: ${person.checkedInAt ? new Date(person.checkedInAt).toLocaleString() : 'N/A'}`, w / 2, h - 4, { align: 'center' });

  pdf.save(`badge_${person.tagCode || 'unknown'}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

/** Print Avery 5163 property labels with QR code + Code 128B barcode + triage color */
export async function printPropertyLabels(people: Individual[], incidentName = '') {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const cols = 2, rows = 5;
  const labelsPerPage = cols * rows;
  const labelW = 101.6, labelH = 50.8;
  const marginX = (pageW - cols * labelW) / 2;
  const marginY = (pageH - rows * labelH) / 2;
  const navy: [number, number, number] = [17, 45, 78];
  const triageColors: Record<string, [number, number, number]> = {
    red: [220, 38, 38], yellow: [217, 119, 6], green: [22, 163, 74],
  };

  // Pre-generate QR codes for unique individuals
  const qrCache: Record<string, string> = {};
  const uniqueTags = [...new Set(people.map(p => p.tagCode).filter(Boolean))];
  for (const tag of uniqueTags) {
    try {
      qrCache[tag] = await QRCode.toDataURL(tag, {
        width: 200, margin: 1, errorCorrectionLevel: 'M',
        color: { dark: '#112D4E', light: '#ffffff' },
      });
    } catch { /* skip if QR gen fails */ }
  }

  // Single person → fill page with copies
  const labelData = people.length === 1
    ? Array(labelsPerPage).fill(people[0])
    : people;

  labelData.forEach((p: Individual, idx: number) => {
    const posOnPage = idx % labelsPerPage;
    const col = posOnPage % cols;
    const row = Math.floor(posOnPage / cols);
    if (idx > 0 && posOnPage === 0) pdf.addPage();

    const x = marginX + col * labelW;
    const y = marginY + row * labelH;

    // Cut guide
    pdf.setDrawColor(210, 210, 210);
    pdf.setLineWidth(0.2);
    pdf.rect(x, y, labelW, labelH);

    // Triage color strip
    const tc = triageColors[(p.triage || 'var(--color-success)').toLowerCase()] || navy;
    pdf.setFillColor(...tc);
    pdf.rect(x, y, 4, labelH, 'F');

    // QR code (right side)
    const qrDataUrl = qrCache[p.tagCode];
    if (qrDataUrl) {
      pdf.addImage(qrDataUrl, 'PNG', x + labelW - 30, y + 2, 26, 26);
    }

    // Tag code (large, top left)
    pdf.setFont('courier', 'bold');
    pdf.setFontSize(13);
    pdf.setTextColor(...navy);
    pdf.text(p.tagCode || '', x + 10, y + 10);

    // Name
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...navy);
    pdf.text(`${p.firstName || ''} ${p.lastName || ''}`.trim(), x + 10, y + 20);

    // DOB + Age + Gender
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    const age = calcAge(p.dob);
    const dobLine = [
      p.dob ? `DOB: ${new Date(p.dob).toLocaleDateString()}` : '',
      age != null ? `Age: ${age}` : '',
      p.gender || '',
    ].filter(Boolean).join('  ·  ');
    if (dobLine) pdf.text(dobLine, x + 10, y + 28);

    // Triage badge
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...tc);
    pdf.text(`TRIAGE: ${(p.triage || 'var(--color-success)').toUpperCase()}`, x + 10, y + 36);

    // Incident name (bottom left)
    if (incidentName) {
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(148, 163, 184);
      pdf.text(incidentName, x + 10, y + labelH - 4);
    }

    // Branding (bottom right)
    pdf.setFontSize(6.5);
    pdf.setTextColor(148, 163, 184);
    pdf.text('OpenGov EMC', x + labelW - 6, y + labelH - 4, { align: 'right' });
  });

  pdf.save(`badges_${new Date().toISOString().slice(0, 10)}.pdf`);
}

export default function Roster() {
  const { individuals, loading, loadIndividuals, updateIndividual, addTriageEntry, deleteIndividual } = useIntakeStore();
  const { activeIncident, user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [triageFilter, setTriageFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('datecreated');
  const [sortDesc, setSortDesc] = useState(true);
  const [rosterPage, setRosterPage] = useState(1);

  // Detail modal state
  const [selected, setSelected] = useState<Individual | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newTriage, setNewTriage] = useState('');
  const [triageReason, setTriageReason] = useState('');
  const [triageLoading, setTriageLoading] = useState(false);

  // Edit mode state
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Individual>>({});
  const [saveLoading, setSaveLoading] = useState(false);

  // Edit photo / camera state
  const [editCameraActive, setEditCameraActive] = useState(false);
  const [editPhotoPreview, setEditPhotoPreview] = useState<string>('');
  const editVideoRef = useRef<HTMLVideoElement>(null);
  const editStreamRef = useRef<MediaStream | null>(null);

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (activeIncident) {
      loadIndividuals(String(activeIncident.id));
    }
  }, [activeIncident, loadIndividuals]);

  // Handle ?highlight=id from dashboard clicks
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && individuals.length > 0) {
      const ind = individuals.find(i => String(i.id) === highlightId);
      if (ind) {
        openDetail(ind);
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, individuals, setSearchParams]);

  const incidentIndividuals = useMemo(() => {
    if (!activeIncident) return [];
    return individuals.filter(i => String(i.incidentId) === String(activeIncident.id));
  }, [individuals, activeIncident]);

  const filtered = useMemo(() => {
    let list = incidentIndividuals;

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        (i.firstName || '').toLowerCase().includes(q) ||
        (i.lastName || '').toLowerCase().includes(q) ||
        (i.tagCode || '').toLowerCase().includes(q) ||
        (i.dob || '').includes(q)
      );
    }

    if (triageFilter !== 'all') {
      list = list.filter(i => (i.triage || 'var(--color-success)') === triageFilter);
    }
    if (statusFilter !== 'all') {
      list = list.filter(i => (i.status || 'active') === statusFilter);
    }

    // Sort
    list = [...list].sort((a, b) => {
      const aVal = (a[sortKey] ?? '').toString().toLowerCase();
      const bVal = (b[sortKey] ?? '').toString().toLowerCase();
      const cmp = aVal.localeCompare(bVal);
      return sortDesc ? -cmp : cmp;
    });

    return list;
  }, [incidentIndividuals, search, triageFilter, statusFilter, sortKey, sortDesc]);

  // Reset page when filters change
  useEffect(() => { setRosterPage(1); }, [search, triageFilter, statusFilter]);

  const rosterTotalPages = Math.max(1, Math.ceil(filtered.length / ROSTER_PAGE_SIZE));
  const pagedRoster = filtered.slice((rosterPage - 1) * ROSTER_PAGE_SIZE, rosterPage * ROSTER_PAGE_SIZE);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDesc(!sortDesc);
    else { setSortKey(key); setSortDesc(false); }
  };

  const openDetail = (ind: Individual) => {
    setSelected(ind);
    setNewTriage(ind.triage || 'var(--color-success)');
    setTriageReason('');
    setEditing(false);
    setEditData({});
    setModalOpen(true);
  };

  const closeDetail = () => {
    setModalOpen(false);
    setSelected(null);
    setNewTriage('');
    setTriageReason('');
    setEditing(false);
    setEditData({});
    setDeleteConfirm(false);
    editStreamRef.current?.getTracks().forEach(t => t.stop());
    editStreamRef.current = null;
    if (editVideoRef.current) editVideoRef.current.srcObject = null;
    setEditCameraActive(false);
    setEditPhotoPreview('');
  };

  const startEditing = () => {
    if (!selected) return;
    setEditData({
      firstName: selected.firstName,
      lastName: selected.lastName,
      dob: selected.dateOfBirth || selected.dob,
      gender: selected.gender,
      medicalNeeds: selected.medicalNeeds,
      medications: selected.medications,
      emergencyContact: selected.emergencyContactName || selected.emergencyContact,
      contactPhone: selected.emergencyContactPhone || selected.contactPhone,
      notes: selected.notes,
      photoUrl: selected.photoUrl || '',
    });
    setEditPhotoPreview(selected.photoUrl || '');
    setEditing(true);
  };

  const startEditCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      editStreamRef.current = stream;
      if (editVideoRef.current) {
        editVideoRef.current.srcObject = stream;
        editVideoRef.current.play();
      }
      setEditCameraActive(true);
    } catch {
      notifications.show({ title: 'Camera Error', message: 'Could not access camera.', color: 'var(--color-error)' });
    }
  };

  const stopEditCamera = () => {
    editStreamRef.current?.getTracks().forEach(t => t.stop());
    editStreamRef.current = null;
    if (editVideoRef.current) editVideoRef.current.srcObject = null;
    setEditCameraActive(false);
  };

  const captureEditPhoto = () => {
    if (!editVideoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = editVideoRef.current.videoWidth;
    canvas.height = editVideoRef.current.videoHeight;
    canvas.getContext('2d')!.drawImage(editVideoRef.current, 0, 0);
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
      const resized = await resizeImageToBase64(file, 800);
      setEditPhotoPreview(resized);
      setEditData(prev => ({ ...prev, photoUrl: resized }));
      stopEditCamera();
    }, 'image/jpeg', 0.85);
  };

  const handleEditPhotoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const resized = await resizeImageToBase64(file, 800);
    setEditPhotoPreview(resized);
    setEditData(prev => ({ ...prev, photoUrl: resized }));
    e.target.value = '';
  };

  const handleSaveEdit = async () => {
    if (!selected || !activeIncident) return;
    setSaveLoading(true);
    try {
      await updateIndividual(selected.id, editData);
      // Refresh selected from the re-fetched store so the modal shows current data
      const fresh = useIntakeStore.getState().individuals.find(i => i.id === selected.id);
      setSelected(fresh ?? { ...selected, ...editData });
      setEditing(false);
      notifications.show({ title: 'Updated', message: 'Individual record has been updated.', color: 'var(--color-success)' });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to save changes.',
        color: 'var(--color-error)',
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePrintBadge = async (ind: Individual) => {
    await printBadge(ind, activeIncident?.name || '');
    notifications.show({ title: 'Badge PDF', message: 'Badge PDF generated.', color: 'var(--color-primary-700)' });
  };

  const handleTriageChange = async () => {
    if (!selected || !activeIncident || !newTriage || newTriage === selected.triage) return;
    setTriageLoading(true);
    try {
      await updateIndividual(selected.id, { triage: newTriage });
      await addTriageEntry({
        individualId: String(selected.id),
        incidentId: String(activeIncident.id),
        triageLevel: newTriage,
        changedBy: user?.firstName
          ? `${user.firstName} ${user.lastName || ''}`.trim()
          : 'Unknown',
        reason: triageReason,
        timestamp: new Date().toISOString(),
      });
      // Update selected in place for the modal
      setSelected({ ...selected, triage: newTriage });
      setTriageReason('');
    } catch (err) {
      console.error('Triage change failed:', err);
    } finally {
      setTriageLoading(false);
    }
  };

  const handleDeleteIndividual = async () => {
    if (!selected) return;
    setDeleteLoading(true);
    try {
      await deleteIndividual(selected.id);
      notifications.show({ title: 'Deleted', message: 'Individual record has been deleted.', color: 'var(--color-error)' });
      closeDetail();
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to delete.', color: 'var(--color-error)' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const SortIcon = sortDesc ? IconSortDescending : IconSortAscending;

  // ── No incident selected ──────────────────────────────────
  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="#939598">
            <IconAlertTriangle size={30} />
          </ThemeIcon>
          <Title order={4}>No Incident Selected</Title>
          <Text size="sm" c="dimmed">
            Use the incident selector in the top header bar.
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <div>
      {/* ── Header ─────────────────────────────────────────── */}
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Title order={3} c="black">Roster</Title>
          <Badge size="sm" variant="light" color="var(--color-primary-700)">
            {filtered.length} of {incidentIndividuals.length}
          </Badge>
        </Group>
        <Group gap="xs">
          <Button
            size="sm"
            variant="light"
            leftSection={<IconDownload size={14} />}
            onClick={() => exportCSV('intake-roster',
              ['Last Name', 'First Name', 'DOB', 'Tag Code', 'Triage', 'Status', 'Intake Method', 'Date Created', 'Medical Needs', 'Emergency Contact', 'Contact Phone'],
              filtered.map(ind => [ind.lastName, ind.firstName, ind.dob ? ind.dob.slice(0, 10) : '', ind.tagCode, ind.triage, ind.status, ind.intakeMethod, ind.datecreated ? ind.datecreated.slice(0, 10) : '', ind.medicalNeeds, ind.emergencyContact, ind.contactPhone])
            )}
          >
            Export CSV
          </Button>
          <Button
            leftSection={<IconUserPlus size={14} />}
            size="sm"
            component="a"
            href="/intake/register"
          >
            New Registration
          </Button>
        </Group>
      </Group>

      {/* ── Filters ────────────────────────────────────────── */}
      <Paper p="md" radius="sm" withBorder mb="md">
        <Group gap="md" wrap="wrap">
          <TextInput
            placeholder="Search name, tag, DOB..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
          <SegmentedControl
            value={triageFilter}
            onChange={setTriageFilter}
            data={[
              { value: 'all', label: 'All Triage' },
              { value: 'var(--color-success)', label: 'Green' },
              { value: '#C68700', label: 'Yellow' },
              { value: 'var(--color-error)', label: 'Red' },
            ]}
            size="xs"
          />
          <Select
            value={statusFilter}
            onChange={(v) => setStatusFilter(v || 'all')}
            data={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'discharged', label: 'Discharged' },
            ]}
            size="xs"
            w={140}
          />
        </Group>
      </Paper>

      {/* ── Table ──────────────────────────────────────────── */}
      <Paper radius="sm" withBorder style={{ overflow: 'auto' }}>
        <Table highlightOnHover striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('lastName')}>
                <Group gap={4} >Name {sortKey === 'lastName' && <SortIcon size={12} />}</Group>
              </Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('dob')}>
                <Group gap={4} >DOB {sortKey === 'dob' && <SortIcon size={12} />}</Group>
              </Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('triage')}>
                <Group gap={4} >Triage {sortKey === 'triage' && <SortIcon size={12} />}</Group>
              </Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('tagCode')}>
                <Group gap={4} >Tag Code {sortKey === 'tagCode' && <SortIcon size={12} />}</Group>
              </Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>
                <Group gap={4} >Status {sortKey === 'status' && <SortIcon size={12} />}</Group>
              </Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('intakeMethod')}>
                <Group gap={4} >Method {sortKey === 'intakeMethod' && <SortIcon size={12} />}</Group>
              </Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleSort('datecreated')}>
                <Group gap={4} >Registered {sortKey === 'datecreated' && <SortIcon size={12} />}</Group>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text ta="center" c="dimmed" py="lg">
                    {loading ? 'Loading...' : 'No individuals found.'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              pagedRoster.map((ind) => {
                const age = calcAge(ind.dob);
                return (
                  <Table.Tr
                    key={ind.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => openDetail(ind)}
                  >
                    <Table.Td>
                      <Text size="sm" fw={500}>{ind.lastName}, {ind.firstName}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{ind.dob ? ind.dob.slice(0, 10) : '\u2014'}</Text>
                      {age !== null && <Text size="xs" c="dimmed">Age {age}</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        color={ind.triage === 'var(--color-error)' ? 'var(--color-error)' : ind.triage === '#C68700' ? '#C68700' : 'var(--color-success)'}
                        variant="filled"
                      >
                        {(ind.triage || 'var(--color-success)').toUpperCase()}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="outline" color="#939598" ff="monospace">
                        {ind.tagCode}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        variant="light"
                        color={ind.status === 'discharged' ? '#939598' : 'var(--color-primary-700)'}
                      >
                        {ind.status || 'active'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" tt="capitalize">{ind.intakeMethod || '\u2014'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">{formatDateTime(ind.datecreated)}</Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {rosterTotalPages > 1 && (
        <Group  mt="md">
          <Pagination total={rosterTotalPages} value={rosterPage} onChange={setRosterPage} size="sm" />
        </Group>
      )}

      {/* ── Detail Modal ───────────────────────────────────── */}
      <Modal
        opened={modalOpen}
        onClose={closeDetail}
        title={
          selected ? (
            <Group gap="sm">
              <Text fw={600}>{selected.lastName}, {selected.firstName}</Text>
              <Badge
                color={selected.triage === 'var(--color-error)' ? 'var(--color-error)' : selected.triage === '#C68700' ? '#C68700' : 'var(--color-success)'}
                variant="filled"
                size="sm"
              >
                {(selected.triage || 'var(--color-success)').toUpperCase()}
              </Badge>
            </Group>
          ) : 'Individual Detail'
        }
        size="lg"
      >
        {selected && (
          <Stack gap="md">
            {/* Photo + basic info */}
            <Group align="flex-start" gap="md">
              <Avatar
                size={80}
                radius="md"
                src={selected.photoUrl || null}
                color={selected.triage === 'var(--color-error)' ? 'var(--color-error)' : selected.triage === '#C68700' ? '#C68700' : 'var(--color-success)'}
              >
                {selected.firstName?.[0]}{selected.lastName?.[0]}
              </Avatar>
              <Stack gap={4} style={{ flex: 1 }}>
                <Text fw={600} size="lg">
                  {selected.firstName} {selected.lastName}
                </Text>
                <Group gap="sm">
                  <Badge
                    color={selected.triage === 'var(--color-error)' ? 'var(--color-error)' : selected.triage === '#C68700' ? '#C68700' : 'var(--color-success)'}
                    variant="filled"
                    size="sm"
                  >
                    {(selected.triage || 'var(--color-success)').toUpperCase()}
                  </Badge>
                  <Badge
                    variant="light"
                    color={selected.status === 'discharged' ? '#939598' : 'var(--color-primary-700)'}
                    size="sm"
                  >
                    {selected.status || 'active'}
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed">Tag: {selected.tagCode}</Text>
              </Stack>
            </Group>

            {/* Action buttons */}
            <Group gap="xs" wrap="wrap">
              <Button
                variant="light"
                size="xs"
                leftSection={<IconPrinter size={14} />}
                onClick={() => handlePrintBadge(selected)}
              >
                Print Badge
              </Button>
              <Button
                variant="light"
                size="xs"
                color="#939598"
                leftSection={<IconPrinter size={14} />}
                onClick={async () => {
                  await printPropertyLabels([selected], activeIncident?.name || '');
                  notifications.show({ title: 'Labels PDF', message: 'Property labels PDF generated.', color: 'var(--color-primary-700)' });
                }}
              >
                Print Labels
              </Button>
              {!editing && (
                <Button
                  variant="subtle"
                  size="xs"
                  color="var(--color-primary-700)"
                  leftSection={<IconEdit size={14} />}
                  onClick={startEditing}
                  style={{ marginLeft: 'auto' }}
                >
                  Edit Info
                </Button>
              )}
              {user?.isAdmin && !editing && (
                deleteConfirm ? (
                  <Group gap={4}>
                    <Text size="xs" c="var(--color-error)">Delete this person?</Text>
                    <Button size="xs" color="var(--color-error)" loading={deleteLoading} onClick={handleDeleteIndividual}>
                      Confirm Delete
                    </Button>
                    <Button size="xs" variant="subtle" onClick={() => setDeleteConfirm(false)}>Cancel</Button>
                  </Group>
                ) : (
                  <Button
                    variant="subtle"
                    size="xs"
                    color="var(--color-error)"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => setDeleteConfirm(true)}
                  >
                    Delete
                  </Button>
                )
              )}
            </Group>

            <Divider />

            {/* Detail fields — view or edit mode */}
            {editing ? (
              <Stack gap="sm">
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  <TextInput
                    label="First Name"
                    value={editData.firstName || ''}
                    onChange={e => setEditData({ ...editData, firstName: e.target.value })}
                  />
                  <TextInput
                    label="Last Name"
                    value={editData.lastName || ''}
                    onChange={e => setEditData({ ...editData, lastName: e.target.value })}
                  />
                  <TextInput
                    label="Date of Birth"
                    type="date"
                    value={editData.dob || ''}
                    onChange={e => setEditData({ ...editData, dob: e.target.value })}
                  />
                  <Select
                    label="Gender"
                    value={editData.gender || ''}
                    onChange={v => setEditData({ ...editData, gender: v || '' })}
                    data={[
                      { value: 'male', label: 'Male' },
                      { value: 'female', label: 'Female' },
                      { value: 'other', label: 'Other' },
                      { value: 'unknown', label: 'Prefer not to say' },
                    ]}
                    clearable
                  />
                </SimpleGrid>
                <Textarea
                  label="Medical Needs"
                  value={editData.medicalNeeds || ''}
                  onChange={e => setEditData({ ...editData, medicalNeeds: e.target.value })}
                  minRows={2}
                />
                <Textarea
                  label="Medications"
                  value={editData.medications || ''}
                  onChange={e => setEditData({ ...editData, medications: e.target.value })}
                  minRows={2}
                />
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  <TextInput
                    label="Emergency Contact"
                    value={editData.emergencyContact || ''}
                    onChange={e => setEditData({ ...editData, emergencyContact: e.target.value })}
                  />
                  <TextInput
                    label="Contact Phone"
                    value={editData.contactPhone || ''}
                    onChange={e => setEditData({ ...editData, contactPhone: e.target.value })}
                  />
                </SimpleGrid>
                <Textarea
                  label="Notes"
                  value={editData.notes || ''}
                  onChange={e => setEditData({ ...editData, notes: e.target.value })}
                  minRows={2}
                />

                {/* Photo section */}
                <div>
                  <Text size="sm" fw={500} mb={6}>Photo</Text>
                  {editPhotoPreview ? (
                    <Group gap="sm" align="flex-start">
                      <img
                        src={editPhotoPreview}
                        alt="Preview"
                        style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4, border: '1px solid #dee2e6' }}
                      />
                      <Stack gap={4}>
                        <Text size="xs" c="dimmed">Photo ready to save</Text>
                        <Button
                          size="xs"
                          variant="subtle"
                          color="var(--color-error)"
                          leftSection={<IconX size={12} />}
                          onClick={() => {
                            setEditPhotoPreview('');
                            setEditData(prev => ({ ...prev, photoUrl: '' }));
                          }}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </Group>
                  ) : editCameraActive ? (
                    <Stack gap="xs">
                      <video
                        ref={editVideoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{ width: '100%', maxWidth: 320, borderRadius: 4, background: '#000' }}
                      />
                      <Group gap="xs">
                        <Button size="xs" leftSection={<IconCamera size={12} />} onClick={captureEditPhoto}>
                          Capture
                        </Button>
                        <Button size="xs" variant="subtle" color="#939598" onClick={stopEditCamera}>
                          Cancel
                        </Button>
                      </Group>
                    </Stack>
                  ) : (
                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconCamera size={12} />}
                        onClick={startEditCamera}
                      >
                        Take Photo
                      </Button>
                      <Button
                        size="xs"
                        variant="light"
                        color="#939598"
                        leftSection={<IconUpload size={12} />}
                        onClick={() => document.getElementById('edit-photo-upload')?.click()}
                      >
                        Upload
                      </Button>
                      <input
                        id="edit-photo-upload"
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleEditPhotoFile}
                      />
                    </Group>
                  )}
                </div>

                <Group justify="flex-end">
                  <Button variant="subtle" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button
                    size="sm"
                    leftSection={<IconDeviceFloppy size={14} />}
                    onClick={handleSaveEdit}
                    loading={saveLoading}
                  >
                    Save Changes
                  </Button>
                </Group>
              </Stack>
            ) : (
              <>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  <DetailField icon={<IconUser size={14} />} label="Date of Birth" value={
                    selected.dob
                      ? `${new Date(selected.dob).toLocaleDateString()}${calcAge(selected.dob) !== null ? ` (Age ${calcAge(selected.dob)})` : ''}`
                      : '\u2014'
                  } />
                  <DetailField icon={<IconUser size={14} />} label="Gender" value={selected.gender || '\u2014'} />
                  <DetailField icon={<IconMedicalCross size={14} />} label="Medical Needs" value={selected.medicalNeeds || '\u2014'} />
                  <DetailField icon={<IconPill size={14} />} label="Medications" value={selected.medications || '\u2014'} />
                  <DetailField icon={<IconPhone size={14} />} label="Emergency Contact" value={
                    selected.emergencyContact
                      ? `${selected.emergencyContact}${selected.contactPhone ? ` (${selected.contactPhone})` : ''}`
                      : '\u2014'
                  } />
                  <DetailField icon={<IconNotes size={14} />} label="Notes" value={selected.notes || '\u2014'} />
                </SimpleGrid>

                <Text size="xs" c="dimmed">
                  Registered: {formatDateTime(selected.datecreated)} | Method: {selected.intakeMethod || '\u2014'}
                </Text>
              </>
            )}

            {/* ── Change Triage (only for active individuals) ── */}
            {selected.status !== 'discharged' && !editing && (
              <>
                <Divider label="Change Triage Level" labelPosition="left" />

                <Group gap="sm">
                  {TRIAGE_OPTIONS.map((opt) => (
                    <Button
                      key={opt.value}
                      variant={newTriage === opt.value ? 'filled' : 'outline'}
                      color={opt.color}
                      size="xs"
                      onClick={() => setNewTriage(opt.value)}
                      style={
                        newTriage === opt.value
                          ? undefined
                          : { borderColor: TRIAGE_COLORS[opt.value], color: TRIAGE_COLORS[opt.value] }
                      }
                    >
                      {opt.label}
                    </Button>
                  ))}
                </Group>

                <Textarea
                  label="Reason for triage change"
                  placeholder="Brief reason for reclassification..."
                  value={triageReason}
                  onChange={(e) => setTriageReason(e.target.value)}
                  minRows={2}
                  disabled={newTriage === (selected.triage || 'var(--color-success)')}
                />

                <Button
                  onClick={handleTriageChange}
                  loading={triageLoading}
                  disabled={!newTriage || newTriage === (selected.triage || 'var(--color-success)') || !triageReason.trim()}
                  color={newTriage === 'var(--color-error)' ? 'var(--color-error)' : newTriage === '#C68700' ? '#C68700' : 'var(--color-success)'}
                >
                  Update Triage to {(newTriage || '').toUpperCase()}
                </Button>
              </>
            )}
          </Stack>
        )}
      </Modal>
    </div>
  );
}

// ── Small helper component for detail fields ──────────────────
function DetailField({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div>
      <Group gap={4} mb={2}>
        {icon}
        <Text size="xs" fw={600} c="dimmed">{label}</Text>
      </Group>
      <Text size="sm">{value}</Text>
    </div>
  );
}
